import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap, type ApiClient } from "./client";

/**
 * Every domain type below is an alias into the generated contract. Aliasing is
 * not copying: change the contract, regenerate, and these names either still
 * resolve or fail to compile.
 */
export type Session = components["schemas"]["Session"];
export type Capability = components["schemas"]["Capability"];
export type ProjectSummary = components["schemas"]["ProjectSummary"];
export type ProjectList = components["schemas"]["ProjectList"];
/**
 * A row of the index, which is NOT a `ProjectSummary` (#439).
 *
 * `GET /v1/projects` has answered `ProjectList` since #433, and `ProjectList`
 * items are `ProjectListItem` — the summary plus `readiness`,
 * `publicationStatus`, `attentionReason`, `nextAction`,
 * `lastMeaningfulActivityAt`, `currentRevision` and `lineage`. The index route
 * typed each row as the thinner `ProjectSummary`, which is structurally valid
 * TypeScript: every extra field arrived on the wire and was silently dropped,
 * with no error, no warning and nothing failing. Alias it here, beside the list
 * it belongs to, so a surface that means the row says so.
 */
export type ProjectListItem = components["schemas"]["ProjectListItem"];
/** How far a Project's authoring has got, most advanced first (#433). */
export type ProjectReadiness = components["schemas"]["ProjectReadiness"];
export type PreviewTargetList = components["schemas"]["PreviewTargetList"];
export type ProjectOverview = components["schemas"]["ProjectOverview"];
export type DraftSummary = components["schemas"]["DraftSummary"];
export type Environment = components["schemas"]["Environment"];
// No `LifecycleState`: #202 PR B2 removed the schema. It was `live | draft_only`
// on `ProjectSummary`, derived server-side from `lastPromotedAt` — the singular
// live state #29's AC forbids inferring.
/**
 * The canonical Project lifecycle (#650) — NOT the removed `LifecycleState`
 * the note above records. That one was a deployment claim the Editor cannot
 * make; this one is the ratified §3 authoring/publication ladder, whose
 * `published` still ends at the target-neutral artifact and observes no
 * runtime. It carries one state, the operative candidate with its
 * server-derived validity and revision number, and one verdict per privileged
 * action — everything #651 renders instead of re-deriving policy client-side.
 */
export type ProjectLifecycle = components["schemas"]["ProjectLifecycle"];
export type ProjectLifecycleState =
  components["schemas"]["ProjectLifecycleState"];
export type ProjectLifecycleCapabilities =
  components["schemas"]["ProjectLifecycleCapabilities"];
/** One verdict: permitted, or refused with its stable `lifecycle.*` reason. */
export type LifecycleActionVerdict =
  ProjectLifecycleCapabilities["acceptCandidate"];
export type LifecycleRefusalReason =
  components["schemas"]["LifecycleRefusalReason"];
export type LifecycleCandidate = components["schemas"]["LifecycleCandidate"];
export type CandidateValidity = components["schemas"]["CandidateValidity"];
export type PromotionStatus = components["schemas"]["PromotionStatus"];
export type PromotionStatusList = components["schemas"]["PromotionStatusList"];
/**
 * The Project's immutable history (#432), rendered compactly by #438. Every
 * entry is copied from an authoritative evidence row — never inferred — and
 * the technical block is the bounded coordinate bag for the authorized
 * Technical-details disclosure, never the clinician-facing explanation.
 */
export type ProjectHistoryPage = components["schemas"]["ProjectHistoryPage"];
export type ProjectHistoryEvent = components["schemas"]["ProjectHistoryEvent"];
export type ProjectHistoryEventKind =
  components["schemas"]["ProjectHistoryEventKind"];
export type ProjectHistoryTechnical =
  components["schemas"]["ProjectHistoryTechnical"];

/**
 * The environments a project may be created to target. A strict subset of
 * `Environment`: the contract excludes `prod` from creation so production is
 * not an option the browser has to remember to forbid.
 */
export type NonProdEnvironment = components["schemas"]["NonProdEnvironment"];
export type CreateProjectRequest =
  components["schemas"]["CreateProjectRequest"];
/** The union's discriminator, so the form's mode state is the contract's. */
export type CreateProjectMode = CreateProjectRequest["mode"];

export type DraftWorkspace = components["schemas"]["DraftWorkspace"];
export type DraftVersionPin = components["schemas"]["DraftVersionPin"];
export type ValidationReport = components["schemas"]["ValidationReport"];
export type ValidationFinding = components["schemas"]["ValidationFinding"];
export type ValidationSeverity = components["schemas"]["ValidationSeverity"];
export type SectionSlug = components["schemas"]["SectionSlug"];
export type SectionConfig = components["schemas"]["SectionConfig"];
export type FieldState = components["schemas"]["FieldState"];
export type Provenance = components["schemas"]["Provenance"];
export type LockReason = components["schemas"]["LockReason"];

/**
 * The bounded clinician-readable review contract (#487), which #488 renders.
 *
 * `ClinicalSummarySectionSlug` is deliberately NARROWER than `SectionSlug`. The
 * contract documents it as the six Project review sections with a
 * semantic-summary contract, and the seventh raw navigation rail — `summary` —
 * is outside #487: the service answers 422 for it, asserted by
 * `test_sections_api.py`. Keeping the two unions distinct is what stops
 * `summary` reaching an endpoint that has no summary for it, exactly as
 * `isSectionSlug` stops `questionnaires` reaching `getProjectSection`.
 */
export type ClinicalSectionSummary =
  components["schemas"]["ClinicalSectionSummary"];
export type ClinicalSummaryItem = components["schemas"]["ClinicalSummaryItem"];
export type ClinicalSummarySectionSlug =
  components["schemas"]["ClinicalSummarySectionSlug"];
export type ClinicalSummaryAttention =
  components["schemas"]["ClinicalSummaryAttention"];

export type DraftPatch = components["schemas"]["DraftPatch"];
export type FieldPatch = components["schemas"]["FieldPatch"];

export type CreatePublicationRequest =
  components["schemas"]["CreatePublicationRequest"];

/**
 * The prefix every section's projection is keyed under, for one project.
 *
 * Defined here rather than inline so `queryKeys.section` can be BUILT from it:
 * the two must stay in a prefix relationship, and the only way to guarantee
 * that is to make one literally contain the other. Re-key sections and the
 * prefix follows.
 */
/**
 * One preview run's compact envelope (#1098), and the root every per-run read
 * (#1099's event reads) is keyed beneath. Hoisted so the prefix relationship
 * is literal containment rather than a convention.
 */
const previewRunKey = (projectId: string, previewRunRef: string) =>
  ["projects", projectId, "preview-runs", previewRunRef] as const;

const projectSectionsKey = (projectId: string) =>
  ["projects", projectId, "sections"] as const;

/**
 * The project's publications, and the prefix every publication-scoped read is
 * keyed under.
 *
 * Built once and reused for the same reason `projectSectionsKey` is: the
 * approval-evidence key must stay a strict EXTENSION of this one, so a single
 * `invalidateQueries` on the listing reaches the evidence read that hangs off
 * whichever publication the listing selected. A publication that stopped being
 * current and the evidence about it are one subject read from two ends, and
 * refreshing one without the other is how a withdrawn handoff action stays on
 * screen (#445).
 */
const projectPublicationsKey = (projectId: string) =>
  ["projects", projectId, "publications"] as const;

/**
 * The prefix a project's frozen candidates are keyed under.
 *
 * Hoisted for the same reason as `projectSectionsKey` above, and with the same
 * consequence: `queryKeys.previewCandidateContents` is BUILT from it, so one
 * candidate's contents are guaranteed to sit under the listing's key rather
 * than beside it. The freeze control's single invalidation of the listing
 * therefore reaches them too, which is what it should do — a freeze does not
 * change an existing candidate's bytes, but it does change which candidate a
 * clinician is being asked about.
 */
const previewCandidatesKey = (projectId: string) =>
  ["projects", projectId, "preview-candidates"] as const;
const candidateRuntimeDetectionsKey = (projectId: string) =>
  [...previewCandidatesKey(projectId), "runtime-detection"] as const;

export const queryKeys = {
  session: ["session"] as const,
  /**
   * The deployment's revision identity (#490). Its own key rather than a member
   * of `session`: it is read only while the support panel is open, and it must
   * never be invalidated — or served — alongside a session refresh, which
   * happens on every reauthentication and would answer "what is deployed" from
   * a cache that predates the rollout being investigated.
   */
  deploymentIdentity: ["support", "deployment-identity"] as const,
  /**
   * User administration — every read under ONE root, and the reason is the
   * shape of its write. `PUT /v1/subject-roles` changes a person's live grants,
   * which is the listing, the directory enrichment of exactly the subjects the
   * listing names (a new grant names a new subject), and the effective-access
   * summary read off the catalog. `useReplaceSubjectRolesMutation` therefore
   * invalidates `accessRoot` rather than three siblings, and a stale refusal
   * does the same: the refusal is news about the resource, not about the
   * request. Hooks in `api/access.ts`.
   */
  accessRoot: ["access"] as const,
  roleAssignments: ["access", "role-assignments"] as const,
  /** The server's role vocabulary and matrix — policy, moving with a deployment. */
  roleCatalog: ["access", "role-catalog"] as const,
  /**
   * Keyed by the SORTED, de-duplicated subject list so two renders naming the
   * same people share one entry. Under the access root: a grant names a new
   * subject and the enrichment must follow the listing.
   */
  directoryResolve: (subjects: readonly string[]) =>
    ["access", "directory", "resolve", subjects] as const,
  /**
   * OUTSIDE the access root, deliberately. A directory search answers who
   * exists in the organisation, which no grant changes — so a write must not
   * refetch the picker's page while its dialog is still closing. What a
   * write does change (who already holds access) is read off the listing.
   */
  directorySearch: (query: string) => ["directory", "search", query] as const,
  /**
   * The index listing. `includeArchived` joins `search` in the key (#440) on
   * `libraryAssets`' precedent. The parameter is defaulted so existing callers
   * select the active-list variant without changing their call sites; the key
   * itself intentionally gains the boolean because archived Projects are a
   * different answer to a different question, and one cache entry serving
   * both would show a clinician a list that quietly disagrees with the toggle
   * above it.
   */
  projects: (search: string, includeArchived = false, previewTenantId = "") =>
    [
      "projects",
      {
        search,
        includeArchived,
        // Present only when filtering, so the unfiltered key is unchanged.
        ...(previewTenantId ? { previewTenantId } : {}),
      },
    ] as const,
  /** The per-tenant production picks (ADR 0008 addendum). */
  tenantPicks: ["tenant-picks"] as const,
  /** The Tenants view (#1165): what each preview target runs, and its read-only pack files. */
  tenants: ["tenants"] as const,
  /** The Tenants tab's runtime per environment (#1173): brain, base and overlay in Dev, Staging and Prod. */
  tenantEnvironments: ["tenant-environments"] as const,
  tenantPackFiles: (tenantId: string) =>
    ["tenants", tenantId, "pack-files"] as const,
  tenantPackFile: (tenantId: string, path: string) =>
    ["tenants", tenantId, "pack-files", "content", path] as const,
  previewTargets: ["preview-targets"] as const,
  tenantPacks: ["tenant-packs"] as const,
  /** What a tenant runs now and this editor's environment (the Lab's versions chip). */
  tenantVersions: (tenantId: string) =>
    ["tenants", tenantId, "versions"] as const,
  /** The environments report: per environment, what each tenant runs. */
  environments: ["environments"] as const,
  projectOverview: (projectId: string) =>
    ["projects", projectId, "overview"] as const,
  /** The Project's versioned Summary renderer preferences (#640). */
  projectSummaryPresentation: (projectId: string) =>
    ["projects", projectId, "summary-presentation"] as const,
  /** The Project's versioned Output Guard signal decision (#860). */
  projectResponseFiltering: (projectId: string) =>
    ["projects", projectId, "response-filtering"] as const,
  /**
   * The Project's typed flow settings — dynamic slots, track priority and the
   * session languages (#854, consumed by #855).
   *
   * Under the project root and NOT under `projectSectionsKey`, deliberately:
   * `flow_config.yaml` is a documented omission from the browser's section
   * partition, so no section projection is invalidated by a flow write and this
   * entry is not invalidated by a section one. What the two DO share is the
   * draft revision, which is why the flow writer invalidates the draft and the
   * pin set explicitly rather than relying on a common prefix.
   */
  projectFlow: (projectId: string) => ["projects", projectId, "flow"] as const,
  /**
   * The Project's versioned Summary generation policy (#864). Its own key, and
   * deliberately NOT under `projectSummaryPresentation`: the two are different
   * documents with different writers, and sharing a cache entry would make a
   * report-layout save look like it moved the clinical policy.
   */
  projectSummaryPolicy: (projectId: string) =>
    ["projects", projectId, "summary-policy"] as const,
  /** The Project's effective pack files (list) and one file's content. */
  packFiles: (projectId: string) =>
    ["projects", projectId, "pack-files"] as const,
  packFile: (projectId: string, path: string) =>
    ["projects", projectId, "pack-files", "content", path] as const,
  /** Under `packFiles`, so any file write or revert refreshes it too. */
  companionCheck: (projectId: string) =>
    ["projects", projectId, "pack-files", "companion-check"] as const,
  /**
   * The Project's governed final safety-questioning state, evidence and served
   * consequence document (#851), read by every surface that must warn while it
   * is off (#853).
   *
   * ONE key for four surfaces — the Project header, Safety, validation and
   * preview all render from this entry — because a warning that appeared on
   * one screen and not the next would be worse than no warning at all. The
   * write invalidates it, and the surfaces observe the same cache.
   */
  projectFinalQuestioning: (projectId: string) =>
    ["projects", projectId, "final-questioning"] as const,
  /**
   * Every clinical component of the Project's effective configuration, bound to
   * one configuration identity (#1085, rendered by #1086).
   *
   * A SIBLING KEY WITH NO INVALIDATION LIST, deliberately, and the freshness is
   * bought at mount instead. The note on `invalidateProjectHistory` above
   * records the mechanic this rests on: nesting a key under `projects` does NOT
   * make the narrow sibling invalidations most mutations issue reach it.
   *
   * This response is composed from five coordinates owned by five different
   * writers — draft, pin set, governed settings, base and posture — so
   * "invalidate it from every writer" means six settle paths that must all
   * remember a screen none of them renders, and the one that forgets leaves
   * this surface describing a configuration that no longer exists. What makes
   * that unnecessary is that the surface holds no write control at all: every
   * mutation happens on another section, so returning here REMOUNTS the query,
   * and `refetchOnMount: "always"` re-reads unconditionally. That also covers
   * the writes this application never sees — another tab, another clinician.
   */
  projectClinicalInventory: (projectId: string) =>
    ["projects", projectId, "clinical-inventory"] as const,
  /**
   * The Project's immutable history (#432/#438). Under the project root on
   * purpose: broad Project invalidations (including archive/restore) reach it.
   * Mutations that invalidate only a narrower sibling projection call
   * `invalidateProjectHistory` explicitly after their evidence row commits.
   */
  projectHistory: (projectId: string) =>
    ["projects", projectId, "history"] as const,
  /**
   * Durable Preview discovery (#1138). A remount always re-reads this key: the
   * server-owned correlation table is the source, never a previous visit's
   * React cache or the link the launch dialog returned.
   */
  previewHistory: (projectId: string) =>
    ["projects", projectId, "preview-runs"] as const,
  projectPromotions: (projectId: string) =>
    ["projects", projectId, "promotions"] as const,
  projectPublications: projectPublicationsKey,
  approvalEvidence: (projectId: string, publicationId: string) =>
    [
      ...projectPublicationsKey(projectId),
      publicationId,
      "approval-evidence",
    ] as const,
  /**
   * The project's frozen candidates (#261's selector) and the preview target's
   * synthetic fixtures.
   *
   * Both are keyed UNDER the project rather than beside it, so broad Project
   * invalidations reach them and a second project open in another tab cannot
   * serve either list. The freeze control also invalidates the candidate
   * listing directly because that is the row its write appends.
   */
  previewCandidates: previewCandidatesKey,
  /**
   * One frozen candidate's verified contents (#435).
   *
   * Keyed by the CANDIDATE as well as the project, because the response is
   * immutable per candidate: the archive is hash-verified against the row
   * before a byte is read, so two reads of one candidate cannot disagree and
   * two candidates must never share a cache entry. That is also what makes the
   * review surface's target explicit — a key that changed while the id stayed
   * put, or the reverse, is exactly the silent retarget #435 exists to prevent.
   */
  previewCandidateContents: (projectId: string, candidateId: string) =>
    [...previewCandidatesKey(projectId), candidateId, "contents"] as const,
  /**
   * Every candidate's runtime-detection state for this Project, as a PREFIX.
   *
   * Nothing fetches this key; it exists to be invalidated. The answers under it
   * are derived against the Project's CURRENT preview target, so a retarget
   * makes every one of them describe a target the Project no longer has —
   * which is why the discriminator sits ABOVE the candidate id here, unlike
   * `previewCandidateContents`. Without that ordering there is no prefix that
   * reaches these entries without also refetching the listing and any open
   * contents, both of which a retarget leaves untouched.
   */
  candidateRuntimeDetections: candidateRuntimeDetectionsKey,
  /**
   * One frozen candidate's runtime-detection state for the Project's CURRENT
   * preview target (#853).
   *
   * The candidate id is in the key because the posture half is immutable per
   * candidate. The target REVISION is deliberately not: a key that carried it
   * would leave each superseded revision's answer in the cache to be served
   * again if the Project were retargeted back, and would make a retarget a
   * cache MISS rendered as a loading state rather than an invalidation. The
   * prefix above is what a retarget invalidates instead.
   */
  candidateRuntimeDetection: (projectId: string, candidateId: string) =>
    [...candidateRuntimeDetectionsKey(projectId), candidateId] as const,
  previewPatients: (projectId: string) =>
    ["projects", projectId, "preview-patients"] as const,
  /**
   * One preview run's compact envelope (#1098), keyed by the opaque locator
   * preview-session creation returned. Under the project root so broad
   * Project invalidations reach it, and by the LOCATOR rather than anything
   * the run is about — the key carries no candidate, patient, target or
   * Liv/Brain session identity, exactly like the address it is read from.
   * Hooks in `api/previewInsights.ts`.
   */
  previewRun: previewRunKey,
  /** The run's pack features and per-turn service calls (Reliv Labs "Services"). */
  previewRunServices: (projectId: string, previewRunRef: string) =>
    [...previewRunKey(projectId, previewRunRef), "services"] as const,
  /**
   * The run's compact event pages, folded into one list (#1102).
   * Beneath the run key so run invalidation reaches it too.
  */
  previewRunEvents: (projectId: string, previewRunRef: string) =>
    [...previewRunKey(projectId, previewRunRef), "events"] as const,
  /**
   * ONE event of a run with its bodies inline (#1099/#1102), keyed by the
   * opaque event ref its authorized compact event page reported. `gcTime: 0`
   * on its hook keeps a body only while a surface shows it.
   */
  previewRunEvent: (
    projectId: string,
    previewRunRef: string,
    eventRef: string,
  ) =>
    [...previewRunKey(projectId, previewRunRef), "events", eventRef] as const,
  /**
   * One page of a run's prompt calls, as the Prompt Inspector's navigation
   * asks for it (#1100). Keyed by the CURSOR and the four filter members as
   * well as the run, because each combination is a different question with a
   * different answer — and never by a page index, which is not what this read
   * is cut on.
   */
  previewRunPromptCalls: (
    projectId: string,
    previewRunRef: string,
    query: Readonly<Record<string, string | number>>,
  ) =>
    [
      ...previewRunKey(projectId, previewRunRef),
      "prompt-calls",
      query,
    ] as const,
  /**
   * ONE prompt call's ordered provenance layers (#1101), keyed by the opaque
   * ref of the call they explain. Under `prompt-calls` because that is where
   * the read sits, and never keyed by the provenance event's own ref — a
   * clinician selects a CALL, and which event explains it is the server's
   * answer rather than part of the question.
   */
  previewRunPromptProvenance: (
    projectId: string,
    previewRunRef: string,
    eventRef: string,
  ) =>
    [
      ...previewRunKey(projectId, previewRunRef),
      "prompt-calls",
      eventRef,
      "provenance",
    ] as const,
  /**
   * ONE provenance layer's frozen authoring document (#1101), keyed by the
   * call it explains and the layer's POSITION — the only two things the
   * browser knows about it. Never by a coordinate or a candidate: both are
   * resolved server-side off the run's own correlation row.
   */
  previewRunPromptSource: (
    projectId: string,
    previewRunRef: string,
    eventRef: string,
    index: number,
  ) =>
    [
      ...previewRunKey(projectId, previewRunRef),
      "prompt-calls",
      eventRef,
      "provenance",
      index,
      "source",
    ] as const,
  /**
   * One preview run's `turn` events, read as compact events (#1105). BUILT
   * from the run's key so an invalidation of the run reaches its turns, and
   * keyed by the locator alone for the same reason the run is. Hooks in
   * `api/previewReplay.ts`.
  */
  previewRunTurns: (projectId: string, previewRunRef: string) =>
    [...previewRunKey(projectId, previewRunRef), "turns"] as const,
  draft: (projectId: string) => ["projects", projectId, "draft"] as const,
  /**
   * All seven sections' projections at once, as a prefix rather than a query.
   *
   * Nothing FETCHES this key; it exists to be invalidated. The pin set and the
   * projection are one subject read from two ends — which asset supplies which
   * field — so a successful `PUT /adoptions` changes lock and provenance in
   * every section, not only in whichever one a surface happens to be showing.
   * `useReplaceProjectAdoptionsMutation` invalidates it for exactly that reason
   * (#372).
   */
  projectSections: projectSectionsKey,
  section: (projectId: string, section: string) =>
    [...projectSectionsKey(projectId), section] as const,
  /**
   * The bounded review payload for one section (#487/#488).
   *
   * Under `projectSectionsKey`, so the invalidation
   * `useReplaceProjectAdoptionsMutation` already performs after a successful
   * `PUT /adoptions` reaches it too. The summary is a REDUCTION of the same
   * projection — a pin change alters which item supplies a section, at which
   * version, in which state — so a pin write that refreshed the raw projection
   * and left this serving the previous adoption would show a clinician the
   * version they just replaced.
   */
  sectionSummary: (projectId: string, section: string) =>
    [...projectSectionsKey(projectId), section, "summary"] as const,
  /**
   * The project's own pin set (#35). Under the project root, not the library
   * one: an archive or a publish invalidates `["library"]` wholesale, and a
   * pin set is not stale merely because an asset it does not pin moved.
   */
  projectAdoptions: (projectId: string) =>
    ["projects", projectId, "adoptions"] as const,
  /**
   * The dry run of ONE adoption decision (#632's preflight), consumed by the
   * decision dialog to state what a pin would replace BEFORE the write.
   *
   * Under the pin set's own key, so the invalidation every adoption writer
   * performs after a successful write reaches it: a preflight decided against
   * one pin set says nothing about the next. Keyed by the write lane and the
   * exact items, so two decisions never share an answer.
   */
  adoptionPreflight: (
    projectId: string,
    lane: string,
    items: readonly { assetId: string; version: string }[],
  ) => ["projects", projectId, "adoptions", "preflight", lane, items] as const,
  /**
   * Library keys all live under the "library" root so one invalidation after
   * an archive or unarchive reaches the catalog (both `includeArchived`
   * variants), the asset, and its adoption view together. Hooks in
   * `api/library.ts`.
   */
  libraryAssets: (includeArchived: boolean) =>
    ["library", "assets", { includeArchived }] as const,
  libraryAsset: (assetId: string) => ["library", "assets", assetId] as const,
  assetVersions: (assetId: string) =>
    ["library", "assets", assetId, "versions"] as const,
  assetVersion: (assetId: string, version: string) =>
    ["library", "assets", assetId, "versions", version] as const,
  assetAdoptions: (assetId: string) =>
    ["library", "assets", assetId, "adoptions"] as const,
  /**
   * One Rules version's substitution tables, composed against base canon (#910).
   *
   * UNDER the version rather than beside it: the projection is OF that immutable
   * version, so it shares the version read's lifetime and a publish invalidates
   * both together through the `["library"]` root the version mutation already
   * clears. A key outside that root would keep serving the previous head's
   * inherited/authored split after a save.
   */
  assetVersionSubstitutions: (assetId: string, version: string) =>
    [
      "library",
      "assets",
      assetId,
      "versions",
      version,
      "substitutions",
    ] as const,
  /**
   * One Summary version's effective example set at one language (#915/#916).
   *
   * UNDER the version for `assetVersionSubstitutions`' reason — the join is OF
   * that immutable version, so a publish invalidates it through the `["library"]`
   * root the version mutation already clears — and keyed by LANGUAGE as well,
   * because `<language>/examples` is one part per pack language tree and the
   * inherited floor is per target AND language. One key for both would serve
   * Hebrew's floor for an English set.
   */
  summaryExamples: (assetId: string, version: string, language: string) =>
    [
      "library",
      "assets",
      assetId,
      "versions",
      version,
      "summary-examples",
      language,
    ] as const,
  /**
   * One Pathway version's screening diagnosis rules, composed for reading.
   *
   * UNDER the version for `assetVersionSubstitutions`' reason — the composition
   * is OF that immutable version, so a publish invalidates it through the
   * `["library"]` root the version mutation already clears. Not keyed by
   * language: unlike a Summary example set, one read answers for every language
   * the version carries, because the criteria and the inherited document are
   * resolved per language INSIDE the response.
   */
  screeningRules: (assetId: string, version: string) =>
    [
      "library",
      "assets",
      assetId,
      "versions",
      version,
      "screening-rules",
    ] as const,
  /**
   * The service-owned part manifest (#491), which is NOT per asset.
   *
   * Under the library root so it shares that cache lifetime, and deliberately
   * OUTSIDE `["library", "assets", …]`: it describes the SERVICE's roles, which
   * move with a deployment rather than with anything a clinician publishes, so
   * an archive or a new version has no claim to have changed it.
   */
  libraryPartManifest: ["library", "part-manifest"] as const,
  /**
   * The server-owned placement/policy registry (#611), which is NOT per asset.
   *
   * Beside the part manifest and for its reason: it describes the SERVICE's
   * policy — which languages a family may not omit, where its files land — and
   * moves with a deployment rather than with anything a clinician publishes.
   */
  libraryPlacementRegistry: ["library", "placement-registry"] as const,
  /**
   * The server-enumerated Summary target vocabulary (#863), which is NOT per
   * asset.
   *
   * Beside the placement registry and for its reason: the flat sections are
   * this deployment's engine literals and the diagnostic groups are its mounted
   * pack's registration, so both move with a deployment rather than with
   * anything a clinician publishes. A new Summary prompt changes which targets
   * are OCCUPIED, and that is derived from the asset catalog — never from here.
   */
  summaryTargetCatalog: ["library", "summary-targets"] as const,
  /**
   * The server-owned BG domain enum vocabulary (#913), which is NOT per asset.
   *
   * Beside the placement registry and for its reason: the statuses are the
   * PINNED TRACKER's own, mirrored by `background_domains.BackgroundItemStatus`
   * and pinned to the engine by a real-checkout test, so the list moves with a
   * deployment's engine pin rather than with anything a clinician publishes.
   * Every domain editor open at once therefore shares one cache entry.
   */
  backgroundDomainVocabulary: ["library", "background-vocabulary"] as const,
  alertDestinationRegistry: ["library", "alert-destination-registry"] as const,
  /**
   * The server-owned Summary report-structure registry (#639), which is NOT
   * per project.
   *
   * Beside the placement registry and for its reason: it publishes the
   * SERVICE's Summary policy — the three layers, which class owns each value,
   * the report structure and its runtime mapping — and moves with a deployment
   * rather than with anything a clinician publishes or a project pins. A
   * Project surface reading it therefore shares one cache entry with every
   * other reader, and an adoption write has no claim to have changed it.
   */
  librarySummaryStructure: ["library", "summary-report-structure"] as const,
  /**
   * The Pathway family/default and variant projection (#642's read, #643's
   * surface).
   *
   * INSIDE `["library", "assets", …]`, unlike the two policy reads above, and
   * that placement is what it MEANS rather than a filing preference: this
   * response is catalog content — names, head versions, capabilities and the
   * steps of each exact head — while the manifest and the registry describe the
   * SERVICE and move with a deployment. Every library write already invalidates
   * the `["library"]` root, so both spellings would refetch; only this one says
   * truthfully that a published version can change the answer.
   *
   * Keyed by `includeArchived` for `libraryAssets`' reason: the two answers are
   * different row sets, and one cache entry per filter is what keeps the
   * archived toggle from showing the unarchived list while it refetches.
   */
  libraryPathwayFamilies: (includeArchived: boolean) =>
    ["library", "assets", "pathway-families", { includeArchived }] as const,
};

/**
 * Marks the immutable History projection stale after a browser write commits
 * one of its source rows.
 *
 * Most Project mutations invalidate a narrow sibling key (`overview`,
 * `promotions`, or `preview-candidates`), not the whole Project prefix.
 * Nesting History under `projects` does not make those sibling invalidations
 * reach it, so event-writing mutations call this operation explicitly.
 */
export function invalidateProjectHistory(
  queryClient: QueryClient,
  projectId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.projectHistory(projectId),
  });
}

/**
 * Every clinical section slug the contract defines.
 *
 * `SectionSurface` is shared by Clinical configuration and the library, but
 * only the first has slugs that are members of the contract's `SectionSlug`.
 * Section fetching is therefore opt-in per mounting route, and this guard is
 * what keeps `questionnaires` from being sent to an endpoint that has no such
 * section.
 */
const SECTION_SLUG_SET = {
  "bg-sets": true,
  "chief-complaint": true,
  pathways: true,
  rules: true,
  safety: true,
  alerts: true,
  summary: true,
} satisfies Record<SectionSlug, true>;

export const SECTION_SLUGS = Object.keys(
  SECTION_SLUG_SET,
) as readonly SectionSlug[];

export function isSectionSlug(value: string): value is SectionSlug {
  return (SECTION_SLUGS as readonly string[]).includes(value);
}

/**
 * The six slugs that have a bounded review contract (#487).
 *
 * `summary` is a `SectionSlug` and is NOT here: the service answers 422 for
 * `/section-summaries/summary`, because the seventh rail is a raw projection
 * with no semantic-summary contract. `questionnaires` is in neither union.
 *
 * Written as a `satisfies` record rather than an array so a contract change
 * that adds or renames a member fails the build here, the way `SECTION_SLUG_SET`
 * already does for the raw union.
 */
const CLINICAL_SUMMARY_SECTION_SLUG_SET = {
  "bg-sets": true,
  "chief-complaint": true,
  pathways: true,
  rules: true,
  safety: true,
  alerts: true,
} satisfies Record<ClinicalSummarySectionSlug, true>;

export const CLINICAL_SUMMARY_SECTION_SLUGS = Object.keys(
  CLINICAL_SUMMARY_SECTION_SLUG_SET,
) as readonly ClinicalSummarySectionSlug[];

export function isClinicalSummarySectionSlug(
  value: string,
): value is ClinicalSummarySectionSlug {
  return (CLINICAL_SUMMARY_SECTION_SLUGS as readonly string[]).includes(value);
}

export function useSessionQuery(): UseQueryResult<Session> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.session,
    async queryFn() {
      return unwrap(await client.GET("/v1/session"));
    },
    staleTime: 5 * 60_000,
  });
}

export function useProjectsQuery(
  search: string,
  includeArchived = false,
  previewTenantId = "",
): UseQueryResult<ProjectList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projects(search, includeArchived, previewTenantId),
    async queryFn() {
      // The flag is sent only when true, `useLibraryAssetsQuery`'s shape:
      // the server's default IS the unsent form, and sending `false`
      // explicitly would make two spellings of one request.
      return unwrap(
        await client.GET("/v1/projects", {
          params: {
            query: {
              ...(search ? { search } : {}),
              ...(includeArchived ? { includeArchived: true } : {}),
              // The tenant is a grouping attribute: this narrows the list and
              // grants nothing the unfiltered list does not.
              ...(previewTenantId ? { previewTenantId } : {}),
            },
          },
        }),
      );
    },
  });
}

/** The tenant packs a Project can start from (`GET /v1/tenant-packs`), not a list the browser keeps. */
export function useTenantPacksQuery(): UseQueryResult<
  components["schemas"]["TenantPackList"]
> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.tenantPacks,
    async queryFn() {
      return unwrap(await client.GET("/v1/tenant-packs"));
    },
    staleTime: 0,
  });
}

/**
 * Preview targets this deployment can currently authenticate against (#202).
 *
 * Deployment state rather than Project state, so it carries no project id: it
 * is read before a Project exists, to choose the target creation requires.
 *
 * NOT cached across the session. The list is derived from provisioned
 * credentials, so a newly provisioned target appears and a revoked one
 * disappears without a deployment — a stale list offers a choice the service
 * then refuses 422. `staleTime: 0` is what MAKES that true: the client-wide
 * default is 30s (`queryClient.ts`), so without the override a dialog reopened
 * inside that window renders a cached list and never refetches — the exact
 * case this paragraph claims to avoid (claude review, round 2).
 */
export function usePreviewTargetsQuery(): UseQueryResult<PreviewTargetList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.previewTargets,
    async queryFn() {
      return unwrap(await client.GET("/v1/preview-targets"));
    },
    staleTime: 0,
  });
}

/**
 * Promotion status, separate from the overview on purpose.
 *
 * The overview's `DraftSummary.promotionState` reports the handoff-REQUEST axis
 * and nothing else. The off-band DevOps execution state lives here, and #27
 * keeps the two apart so that no surface can infer a deployment from a request.
 * #376 removed the third, pin-observed axis outright and nothing replaces it.
 * Fetching them separately also means a failure here costs the Overview one
 * card rather than the page.
 */
export function useProjectPromotionsQuery(
  projectId: string,
): UseQueryResult<PromotionStatusList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projectPromotions(projectId),
    async queryFn() {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/promotions", {
          params: { path: { projectId } },
        }),
      );
    },
    enabled: projectId.length > 0,
  });
}

/** The form controls a create error may be attributed to, besides the target. */
export type CreateFailureField =
  | "name"
  | "seedVersion"
  | "sourceProjectId"
  | "includePaths"
  | "tenantPack";

/**
 * What a `retryable` failure says about the ROW (#619, claude review PR #670).
 *
 *   `uncommitted`  the service answered, and its answer means nothing was
 *                  written: the create transaction rolled back, or the key was
 *                  refused before one began. The form may be edited freely.
 *   `unknown`      the browser never saw a usable answer — the connection
 *                  dropped, the wait timed out, a gateway answered for the
 *                  service, or the service crashed. Starlette does not cancel
 *                  a handler on client disconnect, so the request that was
 *                  sent may still commit. The dialog FREEZES the submitted
 *                  body and key until this is resolved: an edit would rotate
 *                  the key while the outstanding request keeps the old one, so
 *                  a late success could navigate to a row the form no longer
 *                  shows, and a retry of the edited body could create a second
 *                  one.
 */
export type RetryableOutcome = "uncommitted" | "unknown";

export type CreateFailure =
  /**
   * A response known to belong on one specific form control (#619). The
   * server names its field in `error.field` — a pointer drawn from a closed
   * whitelist, never content — and the form marks exactly that control
   * instead of showing a message about "these project details" beside four
   * unmarked ones.
   */
  | { kind: "field"; field: CreateFailureField; message: string }
  /**
   * The one 422 that names a field the form owns (#202 PR C). Collapsing it
   * into `validation` left the clinician stuck: a target revoked between the
   * listing and the submit produced a message naming no field, an unmarked
   * control, a select still offering the dead target, and a resubmit that
   * reproduced it forever — the exact race `usePreviewTargetsQuery`'s
   * `staleTime: 0` exists to shorten (claude review, round 3).
   */
  | { kind: "previewTarget"; message: string }
  /** The service definitely rejected the request, but named no specific field. */
  | { kind: "validation"; message: string }
  | { kind: "denied" }
  | { kind: "unavailable" }
  /**
   * The request did not conclude, or concluded without creating anything —
   * and, because every create now carries an idempotency key, submitting the
   * SAME request again is safe: a request that did commit replays the
   * original project (one row, same id), and one that did not is simply
   * attempted. The form stays mounted with every entered value, and retry is
   * offered (#619). `outcome` decides whether those values may be edited
   * first. This kind replaces the old `ambiguous` dead-end, which existed
   * only because the browser sent no key.
   */
  | { kind: "retryable"; outcome: RetryableOutcome; message: string };

const RETRY_MESSAGE =
  "The request did not complete, so nothing needs to change. Your entries are kept — try again.";

/**
 * The `unknown` outcome's sentence, exported because it is the form-level
 * alert two test files assert on verbatim.
 *
 * It says "may or may not" and names both exits because that is the whole
 * truth from the browser's side: the API offers no non-creating outcome
 * lookup — replaying the key CREATES when the first attempt did not commit —
 * so "Cancel leaves no Project" cannot be promised for an unresolved create,
 * only that the list is refreshed on the way out (see `CreateTimedOutError`
 * for the half the browser can still resolve on its own).
 */
export const UNRESOLVED_CREATE_MESSAGE =
  "The Editor Service did not confirm whether this project was created. The form is locked to the request that was sent: Try again resends it — a project that already landed is reused, never duplicated — and Cancel stops waiting and refreshes the projects list.";

/**
 * Decides what a failed create means, by what came back rather than by status
 * alone — and by the FIELD the server named before any status fallback (#619).
 *
 * `mode` disambiguates the responses that mean different things per mode: a
 * 404 in copy mode is the chosen source project going away (its own control),
 * while in seed mode no control maps to it.
 *
 * HERE and not in `NewProjectDialog.tsx`, because this is the one place the
 * envelope's `field` pointer is read and reduced to a typed outcome — the
 * same #488 discipline `classifySectionFailure` below follows: a `.tsx` on a
 * Project route never reaches for the raw pointer, only for the reduction.
 */
export function classifyCreateFailure(
  error: unknown,
  mode: CreateProjectMode,
): CreateFailure {
  if (error instanceof ApiError) {
    if (error.code === "preview_target_not_enabled") {
      return {
        kind: "previewTarget",
        message:
          "That preview target is no longer available. Choose one from the refreshed list.",
      };
    }
    const namedField = error.envelope["field"];
    if (namedField === "previewTenantId") {
      // A shape-level 422 (target absent from the body) lands here too; the
      // recovery is the same control either way.
      return {
        kind: "previewTarget",
        message:
          "That preview target is no longer available. Choose one from the refreshed list.",
      };
    }
    if (namedField === "seedVersion") {
      return {
        kind: "field",
        field: "seedVersion",
        message:
          "This seed version is not available. Enter a provisioned version, or clear the field to use the latest base seed.",
      };
    }
    if (namedField === "includePaths") {
      return {
        kind: "field",
        field: "includePaths",
        message:
          "That file selection matches nothing in this tenant pack. Choose all files, or a different selection.",
      };
    }
    // The server names `tenantPack` for a pack the target does not run (422) and for a checkout whose
    // files are not the target's (409). Both must reach this control, not the name-conflict fallback below.
    if (namedField === "tenantPack") {
      return {
        kind: "field",
        field: "tenantPack",
        message:
          error.status === 409
            ? "This deployment's tenant pack files are not the files the target runs. Ask an operator to update them, then retry."
            : "The target runs a different tenant pack. Choose the pack the target runs.",
      };
    }
    if (namedField === "sourceProjectId") {
      return {
        kind: "field",
        field: "sourceProjectId",
        message:
          "The selected source project is no longer available. Choose another project to copy.",
      };
    }
    if (error.code === "idempotency_conflict") {
      // Same key, different body — the form rotates its key on every change,
      // so this is a pathological race, not a user mistake. The key was
      // refused before any transaction began, so nothing was written; a
      // fresh key has already been minted by the time this renders (see the
      // dialog's `onError`).
      return {
        kind: "retryable",
        outcome: "uncommitted",
        message: RETRY_MESSAGE,
      };
    }
    if (namedField === "name" || error.status === 409) {
      return {
        kind: "field",
        field: "name",
        message:
          error.status === 409
            ? "A project with this name already exists. Choose another name."
            : "The Editor Service refused this name. Edit it and try again.",
      };
    }
    if (error.status === 404) {
      if (mode === "clone_project") {
        return {
          kind: "field",
          field: "sourceProjectId",
          message:
            "The selected source project is no longer available. Choose another project to copy.",
        };
      }
      return {
        kind: "validation",
        message:
          "The selected seed or source project is no longer available. Review the project details and try again.",
      };
    }
    if (error.status === 422) {
      return {
        kind: "validation",
        message:
          "The Editor Service could not validate these project details. Review the fields and try again.",
      };
    }
    if (error.isForbidden || error.isUnauthenticated) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (
      error.code === "upstream_unavailable" &&
      typeof error.envelope["message"] === "string"
    ) {
      // The SERVICE said so (an envelope came back — a gateway's bare 503
      // has none and falls through to the unknown outcome below). It raises
      // this for more than one dependency on the create path: the
      // preview-target credential store (`preview_targets.py`, checked
      // before the transaction), and the seeded BG-set library
      // (`registry.py`, checked INSIDE the create transaction, which rolls
      // back). So "nothing was created" holds for both, but WHICH dependency
      // and what to do about it ("retry" vs "run the seed importer") only the
      // service's own sentence knows; a status-level guess here named the
      // wrong one (claude review, PR #670). The message is written for a caller
      // — `OpaqueDetailError` keeps mount and tenant details out of the
      // credential-store one.
      return {
        kind: "retryable",
        outcome: "uncommitted",
        message: `Nothing was created. ${error.message}`,
      };
    }
    // A 500, or a gateway answering for the service: a status, but not one
    // that says whether the row was written. Fail towards the safe direction
    // — freeze, and let the same key decide on retry.
    return {
      kind: "retryable",
      outcome: "unknown",
      message: UNRESOLVED_CREATE_MESSAGE,
    };
  }
  // A dropped connection, or the wait this mutation imposes running out
  // (`CreateTimedOutError`, whose `outcome` the dialog also listens to). The
  // idempotency key makes the retry safe either way; what it cannot do is
  // make an edit safe, hence `unknown`.
  return {
    kind: "retryable",
    outcome: "unknown",
    message: UNRESOLVED_CREATE_MESSAGE,
  };
}

/**
 * How long the browser waits for `createProject` before declaring its outcome
 * unknown.
 *
 * An abandoned create is not ambiguous anymore (#619): the dialog sends the
 * contract's optional body-level `idempotencyKey` (documented as "additive for
 * PR #57" — it landed in the #13 PR 1.5 contract change, after PR #57's dialog
 * had shipped without one), so a request whose response was lost is safely
 * re-submitted — `registry.create_project` replays same key + same body as
 * the ORIGINAL project rather than creating a second one. Running out of this
 * wait is what turns a hung request into that retryable state instead of an
 * indefinite spinner; it does NOT abort the request (see `CreateTimedOutError`).
 */
export const CREATE_PROJECT_TIMEOUT_MS = 30_000;

/**
 * The browser stopped waiting; the service did not.
 *
 * This mutation used to send `AbortSignal.timeout`, and aborting the fetch
 * threw away the one thing that can RESOLVE a slow create for free: its
 * eventual answer. The request keeps running now, and `outcome` settles the
 * way the service settles it. A late 201 is the committed row — the dialog
 * navigates to it if still open, and the projects query is invalidated either
 * way, AFTER the commit, which is the ordering a refetch at dismiss time
 * cannot guarantee (claude review, PR #670). A late refusal is a definite
 * answer the form can show and unfreeze on. Only a dropped connection leaves
 * the outcome unknowable from here.
 */
export class CreateTimedOutError extends Error {
  readonly outcome: Promise<ProjectSummary>;

  constructor(outcome: Promise<ProjectSummary>, timeoutMs: number) {
    super(`createProject did not answer within ${timeoutMs}ms`);
    this.name = "CreateTimedOutError";
    this.outcome = outcome;
  }
}

/**
 * Creates a project from the base seed or by cloning an existing one.
 *
 * The request body is the generated discriminated union, so the two modes
 * cannot be mixed and a mode the contract does not define cannot be sent.
 *
 * `timeoutMs` defaults to the product value; only a test staging a slow
 * service passes another — the wait is not a knob the application turns.
 */
export function useCreateProjectMutation({
  timeoutMs = CREATE_PROJECT_TIMEOUT_MS,
}: { timeoutMs?: number } = {}): UseMutationResult<
  ProjectSummary,
  unknown,
  CreateProjectRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(request: CreateProjectRequest) {
      const outcome = client
        .POST("/v1/projects", { body: request })
        .then((result) => unwrap(result));
      return await new Promise<ProjectSummary>((resolve, reject) => {
        const timer = setTimeout(() => {
          // Whatever the service answers from here on, a row it wrote must
          // reach the index — and this fires after the commit, not before.
          // The bare `["projects"]` root, as `onSuccess` below has always
          // used it: every search variant of the listing must go, and the
          // per-project caches under the same root belong to other projects
          // a create does not change — stale-marking them costs a refetch on
          // their next mount, nothing now.
          outcome.then(
            () => {
              void queryClient.invalidateQueries({ queryKey: ["projects"] });
              // A confirmed create changes more than the Project list: a
              // clone copies exact adoption pins and a from-seed create pins
              // the seeded BG sets. This late-success path runs outside the
              // mutation's ordinary `onSuccess`, so it owes the same library
              // invalidation or adoption counts stay stale after a slow 201.
              void queryClient.invalidateQueries({ queryKey: ["library"] });
            },
            () => undefined,
          );
          reject(new CreateTimedOutError(outcome, timeoutMs));
        }, timeoutMs);
        outcome.then(
          (created) => {
            clearTimeout(timer);
            resolve(created);
          },
          (error: unknown) => {
            clearTimeout(timer);
            reject(error instanceof Error ? error : new Error(String(error)));
          },
        );
      });
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      // A create writes more than the project row: a clone copies its source's
      // adoption pins and a from-seed create pins every seeded BG set, so
      // every adoption count the library surfaces render just changed too.
      // Without this, a catalog on screen kept a pre-clone count for up to
      // `staleTime` — the stale half of the audit's PW-LIB-10 (#648).
      void queryClient.invalidateQueries({ queryKey: ["library"] });
    },
    // No AUTOMATIC retry: the idempotency key the dialog sends makes a retry
    // safe, but it stays a decision the clinician takes from the rendered
    // failure — a silent background resubmit would race their edits (#619).
    retry: false,
  });
}

/**
 * The first request is the ticket's exact three-event cap. Once the clinician
 * widens the card, continuation pages use a still-bounded 20 rows to balance
 * the "Show older" walk against request count. Exported so the card's slice
 * and the first request cannot drift into two different definitions of "three".
 */
export const PROJECT_HISTORY_INITIAL_COUNT = 3;
const HISTORY_CONTINUATION_PAGE_LIMIT = 20;

/**
 * The Project's immutable history, newest first (#432), as an infinite query —
 * the first `useInfiniteQuery` in this file, and deliberately: the contract is
 * keyset-paged (`nextCursor` passed back verbatim, absent on the last page),
 * which is exactly the shape TanStack's infinite query models. Pages already
 * fetched never change under concurrent appends (the server's own guarantee),
 * so accumulated pages stay a consistent suffix and a refetch-from-top is how
 * new entries appear. Browser writes that append evidence invalidate this key
 * through `invalidateProjectHistory`; broad Project invalidations reach it too.
 */
export function useProjectHistoryQuery(
  projectId: string,
): UseInfiniteQueryResult<InfiniteData<ProjectHistoryPage>> {
  const client = useApiClient();
  return useInfiniteQuery({
    queryKey: queryKeys.projectHistory(projectId),
    initialPageParam: null as string | null,
    async queryFn({ pageParam }) {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/history", {
          params: {
            path: { projectId },
            query: {
              limit:
                pageParam === null
                  ? PROJECT_HISTORY_INITIAL_COUNT
                  : HISTORY_CONTINUATION_PAGE_LIMIT,
              // The cursor is server-issued and passed back verbatim, never
              // constructed (the contract's own words); the first page sends
              // none at all.
              ...(pageParam === null ? {} : { cursor: pageParam }),
            },
          },
        }),
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? null,
    enabled: projectId.length > 0,
  });
}

export function useProjectOverviewQuery(
  projectId: string,
  {
    alwaysRefetchOnMount = false,
    pollWhileAwaitingPreview = false,
  }: {
    /** Re-read when a surface describes an overview value as current. */
    alwaysRefetchOnMount?: boolean;
    /**
     * Re-read on an interval and on focus while acceptance waits on a
     * qualifying preview: the evidence lands after the Brain admits the run.
     */
    pollWhileAwaitingPreview?: boolean;
  } = {},
): UseQueryResult<ProjectOverview> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projectOverview(projectId),
    async queryFn() {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/overview", {
          params: { path: { projectId } },
        }),
      );
    },
    enabled: projectId.length > 0,
    ...(alwaysRefetchOnMount ? { refetchOnMount: "always" as const } : {}),
    // Only for that caller, so every other reader keeps the client defaults.
    // Faster than the Overview's 30s: the clinician is waiting on this button.
    ...(pollWhileAwaitingPreview
      ? {
          refetchInterval: (query: { state: { data?: ProjectOverview } }) =>
            awaitingPreview(query.state.data) ? 10_000 : false,
          refetchOnWindowFocus: (query: {
            state: { data?: ProjectOverview };
          }) => (awaitingPreview(query.state.data) ? ("always" as const) : true),
        }
      : {}),
  });
}

function awaitingPreview(overview: ProjectOverview | undefined): boolean {
  const verdict = overview?.lifecycle?.capabilities.acceptCandidate;
  return (
    verdict?.state === "refused" &&
    verdict.reason === "lifecycle.no_qualifying_preview"
  );
}

/**
 * The draft workspace, whose `draftVersion` is the pin sent with every
 * validation run and every freeze.
 *
 * `DraftWorkspace.draftVersion` is required and non-null in the contract, so a
 * successful read always yields something to pin against. What the contract
 * does *not* guarantee is that the report comes back bound to it — see
 * `useCandidateCreationGate`.
 */
export function useDraftQuery(
  projectId: string,
): UseQueryResult<DraftWorkspace> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.draft(projectId),
    async queryFn() {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/draft", {
          params: { path: { projectId } },
        }),
      );
    },
    enabled: projectId.length > 0,
  });
}

/**
 * One section's read, shared by the single-section hook and the all-sections
 * one, so the two can never disagree about its key, its shape or its retry
 * policy — they observe the same cache entry.
 */
function sectionQueryOptions(
  client: ApiClient,
  projectId: string,
  section: SectionSlug | null,
) {
  return {
    queryKey: queryKeys.section(projectId, section ?? ""),
    async queryFn(): Promise<SectionConfig> {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/sections/{section}", {
          params: { path: { projectId, section: section as SectionSlug } },
        }),
      );
    },
    enabled: projectId.length > 0 && section !== null,
  };
}

/**
 * The effective projection for one clinical section: every field with its
 * provenance and lock state.
 *
 * This is the only source of `lock.reason`, which is what makes a locked-floor
 * diagnostic a join rather than a property of the validation report.
 */
export function useSectionQuery(
  projectId: string,
  section: SectionSlug | null,
): UseQueryResult<SectionConfig> {
  const client = useApiClient();
  return useQuery(sectionQueryOptions(client, projectId, section));
}

/**
 * The bounded clinician-readable review payload for one section (#487/#488).
 *
 * A SEPARATE read from `useSectionQuery`, not a transformation of it. The two
 * answer different questions of the same stored state — "what does this section
 * effectively contain, field by field" and "which clinical items supply this
 * section, at which versions and in which state" — and the service composes the
 * second itself. Deriving it in the browser is precisely what #487 exists to
 * stop: the contract's own words are that the browser must not "infer item
 * identity, family, version or provenance from dotted paths".
 *
 * Both reads fail the same way and for the same reasons — they share the
 * service's snapshot path, so a lane with no clinical engine answers 503 to
 * both — and `SectionReview` renders that failure rather than an empty section.
 */
export function useSectionSummaryQuery(
  projectId: string,
  section: ClinicalSummarySectionSlug,
  enabled = true,
): UseQueryResult<ClinicalSectionSummary> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.sectionSummary(projectId, section),
    async queryFn(): Promise<ClinicalSectionSummary> {
      return unwrap(
        await client.GET(
          "/v1/projects/{projectId}/section-summaries/{section}",
          { params: { path: { projectId, section } } },
        ),
      );
    },
    enabled: projectId.length > 0 && enabled,
  });
}

/**
 * Every clinical section's projection at once, for a surface whose subject
 * spans them.
 *
 * A project's ADOPTIONS are one set that reaches into every section, so a
 * surface that manages them cannot pick a section the way `SectionDiagnostics`
 * can — the answer to "did the service report this pin locked, and what did it
 * supply" is spread across all seven. Hence `useQueries` over the contract's own
 * slug list rather than a seventh hand-maintained one.
 *
 * It shares `sectionQueryOptions` and therefore the cache keys with
 * `useSectionQuery`, deliberately: opening a section afterwards reads what this
 * already fetched instead of asking again, and neither hook can end up with a
 * different retry policy for the same query.
 *
 * PARTIAL IS THE NORMAL ANSWER, not an error state — and which answer a stack
 * gives is a DEPLOYMENT fact, not a property of this endpoint. A 200 needs a
 * mounted projection engine (`EDITOR_BRAIN_ENGINE_ROOT`, with
 * `EDITOR_PACKS_REPO_ROOT` / `EDITOR_BASE_PACK_ROOT`).
 * `k8s/overlays/dev/kustomization.yaml` and `k8s/overlays/local/kustomization.yaml`
 * both include `../../components/pack-engine`, which stages exactly those roots,
 * and every section answers 200 there. A stack WITHOUT that component — the
 * component-test lane, CI, and any overlay that omits it — answers
 * 503 `upstream_unavailable` to all seven. Callers are expected to render what
 * they do know and say what they do not, and to assume NEITHER answer: an
 * unread projection is never "the engine reported nothing locked", and a read
 * one is never unreachable.
 */
export type ProjectSections = {
  /** The sections that answered, in contract order. */
  readonly sections: readonly SectionConfig[];
  /** At least one section is still being read. */
  readonly isPending: boolean;
  /**
   * At least one section's retry is held rather than in flight.
   *
   * Reported separately because a paused query keeps `status: "pending"` while
   * nothing is happening — #215's defect, and #327's refinement of it: the
   * retryer also parks on `focusManager.isFocused()`, which is only
   * `document.visibilityState !== "hidden"`. A caller that renders `isPending`
   * as "reading…" would say so indefinitely in a background tab, over a failure
   * the service already answered.
   */
  readonly isPaused: boolean;
  /** At least one section could not be read; `sections` is what remains. */
  readonly isPartial: boolean;
  /** The first failure, for a surface that names why the projection is absent. */
  readonly error: unknown;
  /** The failure a held retry would be retrying, when one is held. */
  readonly failureReason: unknown;
  readonly refetch: () => void;
};

export function useProjectSectionsQuery(projectId: string): ProjectSections {
  const client = useApiClient();
  const results = useQueries({
    queries: SECTION_SLUGS.map((section) =>
      sectionQueryOptions(client, projectId, section),
    ),
  });

  return {
    sections: results.flatMap((result) =>
      result.data === undefined ? [] : [result.data],
    ),
    isPending: results.some((result) => result.isPending),
    isPaused: results.some((result) => result.isPaused),
    isPartial: results.some((result) => result.isError),
    error: results.find((result) => result.isError)?.error,
    failureReason: results.find(
      (result) =>
        result.failureReason !== null && result.failureReason !== undefined,
    )?.failureReason,
    refetch: () => {
      for (const result of results) {
        void result.refetch();
      }
    },
  };
}

/**
 * Why the effective projection for a section could not be served.
 *
 * Kinds only — the sentences live with the surface that renders them, keyed by
 * these members with `satisfies`, so a new kind fails the build rather than
 * rendering blank. Exactly the shape `classifyFreezeFailure`,
 * `classifySessionFailure` and `classifyRetargetFailure` carry in `api/preview.ts`,
 * and for the same reason.
 *
 * The two 409s are separate members because the route answers 409 for two
 * unrelated conditions and its own contract text distinguishes them: a STORED
 * IDENTITY that disagrees with the runtime (`projection_identity_mismatch` — a
 * drifted seed pin or base pin, recovered by re-importing or refreshing), and
 * stored state that cannot be composed at all (an unplaceable pin, a stored
 * field with no pack target). Collapsing them would tell a clinician their
 * project is stale when in fact one of its pins cannot be placed, which is a
 * different problem with a different owner.
 *
 * Neither 409 is retryable — `queryClient.ts` lists both codes in
 * `TERMINAL_CONFLICT_CODES` — and neither is `501`. `503` is, and is the only
 * member here that a surface may offer a retry for.
 *
 * Written for #373's Clinical Summary, which is the first surface that has to
 * tell these apart. `SectionDiagnostics` keeps routing its failure through
 * `QueryErrorState`: it renders one generic body for six slugs and has no copy
 * that could name a projection identity, so giving it these distinctions would
 * be scope this ticket does not own.
 */
export type SectionFailure =
  /** 501 — the endpoint is not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  /** 409 `projection_identity_mismatch` — a stored identity is behind the runtime. */
  | { kind: "stale" }
  /**
   * 409 anything else — stored state that cannot be composed at all.
   *
   * WHETHER the envelope named an offending field, never WHICH one. The
   * contract documents `ErrorDetail.field` as "Pointer to the offending input
   * field (a path, never content)", and the summary route's own regression
   * carries `summary.en.fixed_values.title` — so the value is a dotted address,
   * and #488's amendment bans those from every Project route. Both surfaces
   * that render this failure are Project routes.
   *
   * Reduced HERE rather than left to each caller's discretion, because a
   * surface can only be trusted not to render a pointer it does not have. The
   * raw envelope still carries it on `ApiError` for logging and support, which
   * is exactly where the amendment says such data may live.
   */
  | { kind: "uncomposable"; namedField: boolean }
  /** 502/503/504 — engine, pack mounts or draft storage unreachable. Retryable. */
  | { kind: "upstream" }
  | { kind: "denied" }
  /** Anything else, carrying the machine-readable code and the request id only. */
  | { kind: "error"; code: string; requestId: string | undefined };

export function classifySectionFailure(error: unknown): SectionFailure {
  if (error instanceof ApiError) {
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (error.isForbidden || error.isUnauthenticated) {
      return { kind: "denied" };
    }
    if (error.isConflict) {
      if (error.code === "projection_identity_mismatch") {
        return { kind: "stale" };
      }
      return {
        kind: "uncomposable",
        namedField: typeof error.envelope["field"] === "string",
      };
    }
    if (error.status === 502 || error.status === 503 || error.status === 504) {
      return { kind: "upstream" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  // A thrown non-ApiError is a network or runtime failure: no status, no code.
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}
